// Iter81 — Universal product image resolver with priority chain.
//
// Priority (first non-empty value wins):
//   1. product.images[0]          (string or {src|url|image_url})
//   2. product.image_urls[0]      (string list)
//   3. product.image_url          (string)
//   4. product.featured_image     (string or {src|url})
//   5. product.media[0].url       (object list with .url|.src)
//   6. /api/static/blob/{model}/{id}/images  (lazy blob streamer fallback)
//   7. LATM placeholder           (inline SVG — cannot 404)
//
// Resolution respects the Iter79 Cloudflare safety contract: list endpoints
// strip oversized inline base64 blobs and substitute the blob streamer URL.
// This helper preserves the priority chain and ensures `<ProductImg>` never
// renders the brand placeholder when a real image exists somewhere on the
// document.
import React, { useState, useEffect } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export const LIFTED_IMAGE_PLACEHOLDER =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    "<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'>" +
      "<rect width='200' height='200' fill='%231a1f1c'/>" +
      "<path d='M40 140l40-40 30 30 30-30 30 30v40H40z' fill='%231f4d2b' opacity='0.55'/>" +
      "<circle cx='150' cy='60' r='14' fill='%2378e08f' opacity='0.7'/>" +
      "<text x='100' y='180' font-family='Bebas Neue, sans-serif' " +
        "font-size='14' letter-spacing='3' fill='%237eaa84' text-anchor='middle'>LIFTED</text>" +
    "</svg>"
  );

function _resolveUrl(u) {
  if (!u || typeof u !== "string") return null;
  const s = u.trim();
  if (!s) return null;
  if (s.startsWith("data:") || s.startsWith("blob:")) return s;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("/")) return API ? `${API}${s}` : s;
  return s;
}

function _firstFromArray(arr) {
  if (!Array.isArray(arr) || arr.length === 0) return null;
  for (const item of arr) {
    if (typeof item === "string") {
      const r = _resolveUrl(item);
      if (r) return r;
    } else if (item && typeof item === "object") {
      const r = _resolveUrl(item.src || item.url || item.image_url || item.secure_url);
      if (r) return r;
    }
  }
  return null;
}

// Build blob streamer fallback URL. The backend serves
// /api/static/blob/{model}/{id}/{field} — for product cards the safest
// generic field is "images" which the safe_serialize layer already uses.
function _blobStubUrl(product) {
  if (!product) return null;
  const id = product.id || product.handle || product._id;
  if (!id) return null;
  // Many records carry source hints. Pick the most specific one.
  const model =
    product._collection ||
    product.source_collection ||
    (product.owner_partner_id || product.partner_id ? "partner_product_drafts" : null) ||
    (product.handle ? "shopify_products" : null);
  if (!model) return null;
  const base = `/api/static/blob/${model}/${encodeURIComponent(id)}/images`;
  return API ? `${API}${base}` : base;
}

export function getProductImage(product) {
  if (!product) return LIFTED_IMAGE_PLACEHOLDER;
  // 1. images[0]
  const fromImages = _firstFromArray(product.images || product.product_images);
  if (fromImages) return fromImages;
  // 2. image_urls[0]
  const fromUrls = _firstFromArray(product.image_urls);
  if (fromUrls) return fromUrls;
  // 3. image_url
  const fromImageUrl = _resolveUrl(product.image_url);
  if (fromImageUrl) return fromImageUrl;
  // 4. featured_image (string or object)
  if (typeof product.featured_image === "string") {
    const r = _resolveUrl(product.featured_image);
    if (r) return r;
  } else if (product.featured_image && typeof product.featured_image === "object") {
    const r = _resolveUrl(product.featured_image.src || product.featured_image.url);
    if (r) return r;
  }
  // 4b. legacy aliases used elsewhere in the codebase
  const fromImage = _resolveUrl(product.image || product.image_original);
  if (fromImage) return fromImage;
  // 5. media[0].url
  const fromMedia = _firstFromArray(product.media);
  if (fromMedia) return fromMedia;
  // 6. blob streamer fallback
  const stub = _blobStubUrl(product);
  if (stub) return stub;
  // 7. LATM placeholder
  return LIFTED_IMAGE_PLACEHOLDER;
}

// React component wrapper that swaps to LATM placeholder on load failure.
export function ProductImg({ product, alt, className, ...rest }) {
  const [src, setSrc] = useState(() => getProductImage(product));
  useEffect(() => { setSrc(getProductImage(product)); }, [product]);
  return (
    <img
      src={src}
      alt={alt || product?.title || "product"}
      className={className}
      onError={() => { if (src !== LIFTED_IMAGE_PLACEHOLDER) setSrc(LIFTED_IMAGE_PLACEHOLDER); }}
      loading="lazy"
      {...rest}
    />
  );
}
